import groupBy from "lodash-es/groupBy";
import map from "lodash-es/map";
import {
  Activity,
  ActivityType,
  BattleActivityData,
  GameplayEndedActivityData,
  LeaderboardActivityData,
  LoginRewardActivityData,
  RepeatCycle,
  User,
  UserLeaderboardActivityData,
} from "@onmo/graphql/graphql";
import { formatDate } from "./time";
import LbDaily from "../assets/icons/activity_daily_1x.webp";
import LbDaily2x from "../assets/icons/activity_daily_2x.webp";
import LbDaily3x from "../assets/icons/activity_daily_3x.webp";
import LbMonthly from "../assets/icons/activity_monthly_1x.webp";
import LbMonthly2x from "../assets/icons/activity_monthly_2x.webp";
import LbMonthly3x from "../assets/icons/activity_monthly_3x.webp";
import LbWeekly from "../assets/icons/activity_weekly_1x.webp";
import LbWeekly2x from "../assets/icons/activity_weekly_2x.webp";
import LbWeekly3x from "../assets/icons/activity_weekly_3x.webp";
import Fire from "../assets/icons/bold_fire_1x.webp";
import Fire2x from "../assets/icons/bold_fire_2x.webp";
import Fire3x from "../assets/icons/bold_fire_3x.webp";
import { getGameImage } from "./game";
import { getLanguages } from "./settings";

const Month = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
export enum TimeLine {
  Weekly = "Weekly",
  Monthly = "Monthly",
  AllTime = "All time",
}

type ContentCard = {
  src: {
    src1x: string;
    src2x: string;
    src3x: string;
  };
  title: string;
  gem?: number;
  rank?: string;
};

const groupActivitiesByStartedDate = (
  activities: Activity[],
): Record<string, Activity[]> => {
  if (!activities) return {};
  const arenaActivities = map(activities, (activity) => {
    return {
      ...activity,
      displayDate: formatDate(activity.createdAt, "yyyy-mm-dd"),
    };
  });

  return groupBy(arenaActivities, "displayDate");
};

const getFormatDateLanguage = (user?: User): string => {
  if (!user?.language || user?.language.includes("en")) {
    return "en-GB";
  }
  return user?.language ?? "";
};

const contentCard = (
  activity: Activity,
  t: (key: string, opts?: any) => string,
  language: string,
): ContentCard | undefined => {
  const loginReward = activity.data as LoginRewardActivityData;
  const battleData = activity.data as BattleActivityData;
  const imgLb = Activities.getImageLbActivity(activity);
  const { webp, webp2x, webp3x } = getGameImage("small", battleData.game);
  const { default: defaultLanguage } = getLanguages();
  const regionName = new Intl.DisplayNames(language, {
    type: "language",
  });

  switch (activity.activityType) {
    case ActivityType.LoginReward:
      return {
        src: { src1x: Fire, src2x: Fire2x, src3x: Fire3x },
        title: t("daily_streak"),
        gem: loginReward.prizeAmount,
      };
    case ActivityType.BattleFinish: {
      const metadata = battleData.game.metadata
        ? JSON.parse(battleData.game.metadata)
        : null;

      const defaultLang = defaultLanguage || "en";
      const displayLang = regionName.of(language);
      const displayDefaultLang = regionName.of(defaultLang);
      const metadataKey = displayLang ? `${displayLang}_title` : "";
      const metadataKeyDefault = displayDefaultLang
        ? `${displayDefaultLang}_title`
        : "";

      const gameTitle =
        metadata?.translation?.[metadataKey] ??
        metadata?.translation?.[metadataKeyDefault] ??
        battleData.game.title;
      return {
        src: { src1x: webp ?? "", src2x: webp2x ?? "", src3x: webp3x ?? "" },
        title: gameTitle,
        gem: battleData.prize.amount,
      };
    }
    case ActivityType.LeaderboardEnded:
    case ActivityType.UserLeaderboardEnded:
      return {
        src: { src1x: imgLb.src1x, src2x: imgLb.src2x, src3x: imgLb.src3x },
        title: Activities.getTitleLb(activity, t),
        rank: t("rank", {
          number: (activity.data as UserLeaderboardActivityData)
            .leaderboardRank,
        }),
      };
    case ActivityType.GameplayEnded: {
      const gameData = activity?.data as GameplayEndedActivityData;
      const { webp, webp2x, webp3x } = getGameImage(
        "small",
        gameData?.gamePlay?.game,
      );
      return {
        src: { src1x: webp ?? "", src2x: webp2x ?? "", src3x: webp3x ?? "" },
        title: gameData?.gamePlay?.game?.title,
        gem: gameData.rewards?.[0]?.amount,
      };
    }
  }
};

const renderDateTitle = (date: string, me: User) => {
  if (date === "Today") return date;
  if (me) {
    return new Date(date).toLocaleDateString(getFormatDateLanguage(me), {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  }
};

const isLeaderboardActivity = (activity: Activity) => {
  return ["user_leaderboard_ended", "leaderboard_ended"].includes(
    activity.activityType,
  );
};

const isBattleActivity = (activity: Activity) => {
  return (
    activity.activityType === ActivityType.BattleFinish ||
    activity.activityType === ActivityType.GameplayEnded
  );
};

const getTitleLb = (
  lbActivity: Activity,
  t: (key: string, opts?: any) => string,
) => {
  const date = new Date(
    (
      lbActivity?.data as LeaderboardActivityData | UserLeaderboardActivityData
    ).endDate,
  );

  const thisMonth = Month[date.getUTCMonth()];
  const typeLeaderboard =
    (lbActivity?.data as UserLeaderboardActivityData)?.repeatCycle === "weekly"
      ? TimeLine.Weekly
      : thisMonth;

  return `${t(typeLeaderboard)} ${t("leaderboard")}`;
};

const getImageLbActivity = (lbActivity: Activity) => {
  switch ((lbActivity?.data as UserLeaderboardActivityData)?.repeatCycle) {
    case RepeatCycle.Weekly:
      return {
        src1x: LbWeekly,
        src2x: LbWeekly2x,
        src3x: LbWeekly3x,
      };
    case RepeatCycle.Monthly:
      return {
        src1x: LbMonthly,
        src2x: LbMonthly2x,
        src3x: LbMonthly3x,
      };
    default:
      return {
        src1x: LbDaily,
        src2x: LbDaily2x,
        src3x: LbDaily3x,
      };
  }
};

export const Activities = {
  groupActivitiesByStartedDate,
  renderDateTitle,
  isLeaderboardActivity,
  isBattleActivity,
  contentCard,
  getTitleLb,
  getImageLbActivity,
};
